import React, { useEffect, useMemo } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import i18nInstance from '../../i18n/config';
import { EventsDialogApiProvider } from './context/EventsDialogApiContext';
import CalendarContextProvider from './context/CalendarContextProvider';
import EventDialog from './EventDialog';
import { Mode } from './context/types/Mode';
import { DialogType } from './context/types/DialogType';
import { SxProps, Theme } from '@mui/material';
import { ThemeProvider, createTheme, useTheme } from '@mui/material';
import MuiComponentsConfig from './theme/MuiComponentsConfig';

/**
 * Props para el componente EventsDialog
 * Solo requiere las propiedades esenciales; el resto se maneja internamente mediante APIs
 */
export interface EventsDialogProps {
  /** Estado de apertura del modal */
  open: boolean;
  /** Callback al cerrar el modal */
  onClose: () => void;
  /** URL base de la API */
  baseUrl: string;
  /** ID del espacio */
  spaceId: string;
  /** Idioma de la interfaz */
  language?: 'en' | 'es';
  /** Modo del diálogo: 'create' | 'edit' | 'view' */
  mode?: Mode;
  /** 
   * Tipo de diálogo: 'event' | 'task'
   * Define si el modal trabaja con eventos o tareas.
   * Cuando se combina con hiddenTabs=true, permite contextos específicos (solo eventos o solo tareas).
   */
  type?: DialogType;
  /** ID del calendario seleccionado para eventos (usado en modo view/edit) */
  eventCalendarId?: string;
  /** ID del calendario seleccionado para tareas (usado en modo view/edit) */
  taskCalendarId?: string;
  /** Evento seleccionado para modo edit/view */
  selectedEvent?: any;
  /** Tarea seleccionada para modo edit/view */
  selectedTask?: any;
  /** Estilos personalizados */
  sx?: SxProps<Theme>;
  /** ID del contacto a vincular automáticamente al crear un evento o tarea */
  contactId?: string;
  /** 
   * Oculta las tabs de cambio entre evento/tarea.
   * Útil cuando la aplicación consumidora separa eventos y tareas en diferentes áreas.
   * Ejemplo: Una vista de "Mis Eventos" solo debería mostrar eventos (type='event', hiddenTabs=true)
   *          Una vista de "Mis Tareas" solo debería mostrar tareas (type='task', hiddenTabs=true)
   */
  hiddenTabs?: boolean;
  /** Callback invocado tras cada operación CRUD exitosa (crear, editar, eliminar) */
  onActionComplete?: (
    action: 'created' | 'updated' | 'deleted',
    type: 'event' | 'task',
    data: any,
  ) => void;
  /** Fecha inicial para el evento/tarea (útil al crear desde slot de calendario) */
  initialStartDate?: Date;
  /** Fecha final para el evento/tarea (útil al crear desde slot de calendario) */
  initialEndDate?: Date;
}

/**
 * Contenido interno del EventsDialog
 * Usa CalendarContextProvider para manejar todo internamente
 */
const EventsDialogContent: React.FC<EventsDialogProps> = (props) => {
  const {
    open,
    onClose,
    baseUrl,
    spaceId,
    language = 'en',
    mode = 'create',
    type = 'event',
    eventCalendarId,
    taskCalendarId,
    selectedEvent,
    selectedTask,
    hiddenTabs = false,
    onActionComplete,
    contactId,
    initialStartDate,
    initialEndDate,
  } = props;

  const { i18n } = useTranslation();

  // Cambiar idioma cuando la prop cambie
  useEffect(() => {
    if (language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  // Estabilizar el objeto api con useMemo para evitar re-renders y re-fetches
  // si el padre re-renderiza sin cambiar baseUrl ni spaceId.
  const api = useMemo(() => ({ baseUrl, spaceId }), [baseUrl, spaceId]);

  // El provider siempre se monta para mantener estado entre aperturas y cerradas del modal.
  // El EventDialog internamente controla la visibilidad del modal mediante externalOpen.
  return (
    <EventsDialogApiProvider value={api}>
      <CalendarContextProvider
        externalOpen={open}
        externalOnClose={onClose}
        externalMode={mode}
        externalType={type}
        externalEventCalendarId={eventCalendarId}
        externalTaskCalendarId={taskCalendarId}
        externalSelectedEvent={selectedEvent}
        externalSelectedTask={selectedTask}
        hiddenTabs={hiddenTabs}
        externalOnActionComplete={onActionComplete}
        externalContactId={contactId}
        externalInitialStartDate={initialStartDate}
        externalInitialEndDate={initialEndDate}
      >
        <EventDialog />
      </CalendarContextProvider>
    </EventsDialogApiProvider>
  );
};

/**
 * EventsDialog - Componente para gestión de eventos y tareas
 *
 * Este componente maneja internamente toda la lógica de CRUD de eventos y tareas
 * mediante las APIs. Solo requiere las props esenciales: baseUrl, spaceId, open, onClose.
 * Los calendarios, eventos y tareas se cargan automáticamente desde las APIs.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * MODOS DE USO
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * 1. MODO UNIFICADO (eventos y tareas juntos):
 * ```tsx
 * <EventsDialog
 *   open={isOpen}
 *   onClose={handleClose}
 *   baseUrl="https://api.trii.app/api/v1"
 *   spaceId={spaceId}
 *   language="es"
 *   mode="create"
 *   type="event"
 * />
 * ```
 * En este modo, el usuario puede alternar entre crear eventos o tareas usando las tabs.
 * 
 * 2. MODO SEPARADO (solo eventos O solo tareas):
 * ```tsx
 * // Vista de "Mis Eventos" - solo muestra eventos
 * <EventsDialog
 *   open={isEventDialogOpen}
 *   onClose={handleClose}
 *   baseUrl="https://api.trii.app/api/v1"
 *   spaceId={spaceId}
 *   type="event"
 *   hiddenTabs={true}  // Oculta las tabs de cambio
 *   mode="create"
 * />
 * 
 * // Vista de "Mis Tareas" - solo muestra tareas
 * <EventsDialog
 *   open={isTaskDialogOpen}
 *   onClose={handleClose}
 *   baseUrl="https://api.trii.app/api/v1"
 *   spaceId={spaceId}
 *   type="task"
 *   hiddenTabs={true}  // Oculta las tabs de cambio
 *   mode="create"
 * />
 * ```
 * Este modo es útil cuando la aplicación consumidora separa eventos y tareas
 * en diferentes secciones de la interfaz.
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 * ENDPOINTS UTILIZADOS
 * ═══════════════════════════════════════════════════════════════════════════════
 * Ver documentación completa en: src/components/EventsDialog/services/calendarApi.ts
 * 
 * Eventos:
 * - POST   /api/v1/calendar/{spaceId}/events
 * - GET    /api/v1/calendar/{spaceId}/Events?start={start}&end={end}
 * - GET    /api/v1/calendar/{spaceId}/events/{eventId}
 * - PUT    /api/v1/calendar/{spaceId}/events/{eventId}
 * - PUT    /api/v1/calendar/{spaceId}/events/occurrence/{eventId}
 * - DELETE /api/v1/calendar/{spaceId}/events/{eventId}
 * 
 * Tareas:
 * - POST   /api/v1/calendar/{spaceId}/Task
 * - GET    /api/v1/calendar/{spaceId}/Task/{calendarId}
 * - GET    /api/v1/calendar/{spaceId}/Task/detail/{taskId}
 * - PUT    /api/v1/calendar/{spaceId}/Task/{taskId}
 * - DELETE /api/v1/calendar/{spaceId}/Task/{taskId}
 */
const EventsDialog: React.FC<EventsDialogProps> = (props) => {
  // Permitir que el consumidor pase un theme, o usar el de contexto
  const outerTheme = useTheme();
  // Si no hay theme en contexto, usar createTheme por defecto
  const baseTheme = outerTheme?.palette ? outerTheme : createTheme();
  const theme = MuiComponentsConfig(baseTheme);

  return (
    <ThemeProvider theme={theme}>
      <I18nextProvider i18n={i18nInstance}>
        <EventsDialogContent {...props} />
      </I18nextProvider>
    </ThemeProvider>
  );
};

export default EventsDialog;
